/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, ReactElement, useEffect, useState} from 'react';
import {Accordion, AccordionDetails} from '@material-ui/core';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {MaterialExpandMoreIcon} from '@ion/dls/src/icons/MaterialExpandMoreIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';

// defines the icon positions for the ExpansionPanel
export enum IconPosition {
  Start = 'start',
  End = 'end'
};

// defines the prop definitions for the ExpansionPanel
export interface ExpansionPanelTypes {
  id: string;
  header: any;
  subHeader?: any;
  onToggleExpand: (event: object, expanded: boolean) => void;
  expandedLayer?: string | null;
  details: any;
  summaryRootClass?: string;
  rootClass?: string;
  detailsRootOverride?: string;
  disableExpansion?: boolean;
  unmountOnExit?: boolean;
  iconPosition?: IconPosition;
  // overrides the default expand/collapse affordance (MaterialExpandMoreIcon)
  // for callers whose design uses a different icon - e.g. a chevron-right
  // for the current-theme Map Layers panel's row-per-layer list...
  expandIcon?: ReactElement;
  // a chevron-right expandIcon should point down (90deg) once expanded,
  // not the MaterialExpandMoreIcon's default 180deg flip (which turns a
  // right-pointing chevron into a left-pointing one) ...
  expandIconRotatesDown?: boolean;
  // suppresses the default borderBottom this component draws under its
  // own header once expanded - callers with their own list-level divider
  // (e.g. the current-theme Map Layers panel's row-per-layer list) don't
  // want this second, accordion-owned line too ...
  disableExpandedBorder?: boolean;
};

// defines the styles used by the ExpansionPanel
const useStyles = makeStyles(theme => ({
  wrapperRoot : {
    width           : '100%',
    boxShadow       : '0px 0px !important',
    borderRadius    : '0px !important',
    backgroundColor : 'none !important',
    marginTop       : '0px !important'
  },
  summaryContent : {
    margin : '0 !important'
  },
  detailsRoot : {
    display : 'block',
    padding : '10px 0px 25px'
  },
  expanded : {
    borderBottom : `solid 1px ${dlsColor('grey6')}`
  },
  expandIcon : {
    order : -1
  },
  expandIconRotate : {
    // `!important` is required here - MUI's own default rule
    // (`.MuiAccordionSummary-expandIcon.Mui-expanded`) is a two-class
    // compound selector, which outranks our single custom class on
    // specificity alone regardless of stylesheet injection order ...
    transform  : 'rotate(0deg) !important',
    transition : theme.transitions.create('transform', {duration: theme.transitions.duration.shortest})
  },
  expandIconRotateExpanded : {
    transform : 'rotate(90deg) !important'
  }
}));

const isIconAtStart = (position: IconPosition) => position === IconPosition.Start;

/**
 * @ExpansionPanel
 *
 * Defines the ExpansionPanel component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   import Accordion from '@ion/dls/src/components/accordion/Accordion';
 *   <Accordion children=''
 *     id={}
 *     header={}
 *     details={} />
 * ```
 */
const ExpansionPanelWrapper = forwardRef(({
  id,
  header,
  subHeader,
  details,
  onToggleExpand,
  expandedLayer,
  summaryRootClass,
  rootClass,
  detailsRootOverride,
  iconPosition = IconPosition.End,
  disableExpansion = false,
  unmountOnExit = false,
  expandIcon = <MaterialExpandMoreIcon />,
  expandIconRotatesDown = false,
  disableExpandedBorder = false
}: ExpansionPanelTypes, ref) => {
  const [expanded, setExpanded] = useState(false);
  const classes = useStyles();

  const handleChange = (panel) => function({target}, isExpanded) {
    if (!disableExpansion) {
      onToggleExpand(panel, isExpanded);
    }
  };

  useEffect(() => {
    setExpanded(expandedLayer === id);
  }, [expandedLayer]);

  return (
    <Accordion expanded={expanded} TransitionProps={{unmountOnExit}}
      ref={ref}
      classes={{
        root : classnames(classes.wrapperRoot, rootClass)
      }}
      onChange={handleChange(id)}>
      <AccordionSummary expandIcon={!disableExpansion && expandIcon}
        IconButtonProps={isIconAtStart(iconPosition) ? {
          edge : 'start'
        } : null}
        classes={{
          root       : classnames(expanded && !disableExpandedBorder && classes.expanded, summaryRootClass),
          content    : classes.summaryContent,
          expandIcon : isIconAtStart(iconPosition) ? classes.expandIcon : (
            expandIconRotatesDown
              ? classnames(classes.expandIconRotate, expanded && classes.expandIconRotateExpanded)
              : null
          )
        }}>
        {header}
        {subHeader}
      </AccordionSummary>
      <AccordionDetails classes={{
        root : detailsRootOverride || classes.detailsRoot
      }}>
        {details}
      </AccordionDetails>
    </Accordion>
  );
});

export default ExpansionPanelWrapper;
